import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { 
  Calculator, 
  FileText, 
  Send,
  CheckCircle,
  Package,
  Calendar,
  User,
  Building2,
  Mail,
  Phone,
  ClipboardList
} from 'lucide-react';

const Quote = () => {
  const { t } = useTranslation();
  const { language, isRTL } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1: Project Details
    projectName: '',
    projectType: '',
    projectLocation: '',
    
    // Step 2: Contact Information
    name: '',
    email: '',
    phone: '',
    company: '',
    
    // Step 3: Product Requirements
    categories: [],
    specifications: '',
    quantity: '',
    timeline: '',
    
    // Step 4: Additional Information
    budget: '',
    additionalNotes: ''
  });

  const productCategories = [
    { key: 'building', label: t('products.categories.building') },
    { key: 'electrical', label: t('products.categories.electrical') },
    { key: 'fixtures', label: t('products.categories.fixtures') },
    { key: 'lighting', label: t('products.categories.lighting') },
    { key: 'stones', label: t('products.categories.stones') },
    { key: 'steel', label: t('products.categories.steel') },
    { key: 'sanitary', label: t('products.categories.sanitary') },
    { key: 'tiles', label: t('products.categories.tiles') }
  ];

  const projectTypes = [
    { value: 'residential', label: language === 'en' ? 'Residential' : 'سكني' },
    { value: 'commercial', label: language === 'en' ? 'Commercial' : 'تجاري' },
    { value: 'industrial', label: language === 'en' ? 'Industrial' : 'صناعي' },
    { value: 'infrastructure', label: language === 'en' ? 'Infrastructure' : 'بنية تحتية' }
  ];

  const timelineOptions = [
    { value: 'urgent', label: language === 'en' ? 'Urgent (1-2 weeks)' : 'عاجل (1-2 أسبوع)' },
    { value: 'standard', label: language === 'en' ? 'Standard (2-4 weeks)' : 'قياسي (2-4 أسابيع)' },
    { value: 'flexible', label: language === 'en' ? 'Flexible (1-2 months)' : 'مرن (1-2 شهر)' },
    { value: 'longterm', label: language === 'en' ? 'Long-term (2+ months)' : 'طويل الأمد (2+ شهر)' }
  ];

  const budgetRanges = [
    { value: 'under-50k', label: language === 'en' ? 'Under AED 50,000' : 'أقل من 50,000 درهم' },
    { value: '50k-100k', label: language === 'en' ? 'AED 50,000 - 100,000' : '50,000 - 100,000 درهم' },
    { value: '100k-500k', label: language === 'en' ? 'AED 100,000 - 500,000' : '100,000 - 500,000 درهم' },
    { value: '500k-1m', label: language === 'en' ? 'AED 500,000 - 1,000,000' : '500,000 - 1,000,000 درهم' },
    { value: 'over-1m', label: language === 'en' ? 'Over AED 1,000,000' : 'أكثر من 1,000,000 درهم' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCategoryChange = (categoryKey) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(categoryKey)
        ? prev.categories.filter(cat => cat !== categoryKey)
        : [...prev.categories, categoryKey]
    }));
  };

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      
      // Reset form after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setCurrentStep(1);
        setFormData({
          projectName: '',
          projectType: '',
          projectLocation: '',
          name: '',
          email: '',
          phone: '',
          company: '',
          categories: [],
          specifications: '',
          quantity: '',
          timeline: '',
          budget: '',
          additionalNotes: ''
        });
      }, 5000);
    }, 2000);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
                {t('quote.form.project')} *
              </label>
              <input
                type="text"
                name="projectName"
                value={formData.projectName}
                onChange={handleInputChange}
                required
                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isRTL ? 'text-right' : ''}`}
                placeholder={t('quote.form.project')}
              />
            </div>

            <div>
              <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
                {language === 'en' ? 'Project Type' : 'نوع المشروع'} *
              </label>
              <select
                name="projectType"
                value={formData.projectType}
                onChange={handleInputChange}
                required
                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isRTL ? 'text-right' : ''}`}
              >
                <option value="">
                  {language === 'en' ? 'Select project type' : 'اختر نوع المشروع'}
                </option>
                {projectTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
                {language === 'en' ? 'Project Location' : 'موقع المشروع'} *
              </label>
              <input
                type="text"
                name="projectLocation"
                value={formData.projectLocation}
                onChange={handleInputChange}
                required
                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isRTL ? 'text-right' : ''}`}
                placeholder={language === 'en' ? 'Dubai, UAE' : 'دبي، الإمارات'}
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
                  {t('contact.form.name')} *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isRTL ? 'text-right' : ''}`}
                  placeholder={t('contact.form.name')}
                />
              </div>

              <div>
                <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
                  {t('contact.form.phone')} *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isRTL ? 'text-right' : ''}`}
                  placeholder="+971 50 123 4567"
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
                {t('contact.form.email')} *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isRTL ? 'text-right' : ''}`}
                placeholder={t('contact.form.email')}
              />
            </div>

            <div>
              <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
                {t('contact.form.company')}
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isRTL ? 'text-right' : ''}`}
                placeholder={t('contact.form.company')}
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <label className={`block text-sm font-semibold text-gray-700 mb-4 ${isRTL ? 'text-right' : ''}`}>
                {t('quote.form.category')} * ({language === 'en' ? 'Select multiple' : 'اختر متعدد'})
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {productCategories.map((category) => (
                  <motion.div
                    key={category.key}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-300 ${
                      formData.categories.includes(category.key)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-blue-300'
                    }`}
                    onClick={() => handleCategoryChange(category.key)}
                  >
                    <div className="flex items-center space-x-3 rtl:space-x-reverse">
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                        formData.categories.includes(category.key)
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300'
                      }`}>
                        {formData.categories.includes(category.key) && (
                          <CheckCircle className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <span className={`text-sm font-medium ${isRTL ? 'font-arabic' : ''}`}>
                        {category.label}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <div>
              <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
                {t('quote.form.specifications')}
              </label>
              <textarea
                name="specifications"
                value={formData.specifications}
                onChange={handleInputChange}
                rows={4}
                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${isRTL ? 'text-right' : ''}`}
                placeholder={language === 'en' 
                  ? 'Please provide detailed specifications, sizes, quantities, etc.'
                  : 'يرجى تقديم المواصفات التفصيلية والأحجام والكميات وما إلى ذلك.'
                }
              />
            </div>

            <div>
              <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
                {t('quote.form.timeline')} *
              </label>
              <select
                name="timeline"
                value={formData.timeline}
                onChange={handleInputChange}
                required
                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isRTL ? 'text-right' : ''}`}
              >
                <option value="">
                  {language === 'en' ? 'Select timeline' : 'اختر الجدول الزمني'}
                </option>
                {timelineOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
                {language === 'en' ? 'Budget Range' : 'نطاق الميزانية'}
              </label>
              <select
                name="budget"
                value={formData.budget}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isRTL ? 'text-right' : ''}`}
              >
                <option value="">
                  {language === 'en' ? 'Select budget range (optional)' : 'اختر نطاق الميزانية (اختياري)'}
                </option>
                {budgetRanges.map((range) => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
                {language === 'en' ? 'Additional Notes' : 'ملاحظات إضافية'}
              </label>
              <textarea
                name="additionalNotes"
                value={formData.additionalNotes}
                onChange={handleInputChange}
                rows={5}
                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${isRTL ? 'text-right' : ''}`}
                placeholder={language === 'en' 
                  ? 'Any additional requirements, special requests, or questions?'
                  : 'أي متطلبات إضافية أو طلبات خاصة أو أسئلة؟'
                }
              />
            </div>

            {/* Summary */}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-200">
              <h3 className={`text-xl font-bold text-gray-900 mb-4 ${isRTL ? 'font-arabic text-right' : ''}`}>
                {language === 'en' ? 'Quote Summary' : 'ملخص عرض السعر'}
              </h3>
              <div className={`space-y-2 text-sm ${isRTL ? 'text-right font-arabic' : ''}`}>
                <p><strong>{language === 'en' ? 'Project:' : 'المشروع:'}</strong> {formData.projectName}</p>
                <p><strong>{language === 'en' ? 'Contact:' : 'الاتصال:'}</strong> {formData.name} ({formData.email})</p>
                <p><strong>{language === 'en' ? 'Categories:' : 'الفئات:'}</strong> {formData.categories.length} {language === 'en' ? 'selected' : 'مختارة'}</p>
                <p><strong>{language === 'en' ? 'Timeline:' : 'الجدول الزمني:'}</strong> {formData.timeline}</p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen pt-32 pb-20 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className={`text-center max-w-2xl mx-auto px-4 ${isRTL ? 'font-arabic' : ''}`}
        >
          <div className="w-24 h-24 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {language === 'en' ? 'Quote Request Submitted!' : 'تم إرسال طلب عرض السعر!'}
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            {language === 'en' 
              ? 'Thank you for your interest! Our team will review your requirements and get back to you within 24 hours with a detailed quote.'
              : 'شكراً لك على اهتمامك! سيقوم فريقنا بمراجعة متطلباتك والرد عليك خلال 24 ساعة بعرض سعر مفصل.'
            }
          </p>
          <div className="bg-blue-50 rounded-lg p-6 text-left">
            <h3 className="font-bold text-gray-900 mb-2">
              {language === 'en' ? 'What happens next?' : 'ما الذي سيحدث بعد ذلك؟'}
            </h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-center space-x-2 rtl:space-x-reverse">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>
                  {language === 'en' 
                    ? 'Our experts will analyze your requirements'
                    : 'سيقوم خبراؤنا بتحليل متطلباتك'
                  }
                </span>
              </li>
              <li className="flex items-center space-x-2 rtl:space-x-reverse">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>
                  {language === 'en' 
                    ? 'We\'ll prepare a detailed quote with competitive pricing'
                    : 'سنعد عرض سعر مفصل بأسعار تنافسية'
                  }
                </span>
              </li>
              <li className="flex items-center space-x-2 rtl:space-x-reverse">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>
                  {language === 'en' 
                    ? 'A dedicated account manager will contact you'
                    : 'سيتصل بك مدير حساب مخصص'
                  }
                </span>
              </li>
            </ul>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-purple-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-islamic-pattern opacity-10" />
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className={`max-w-4xl mx-auto ${isRTL ? 'font-arabic' : ''}`}
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {t('quote.title')}
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              {t('quote.subtitle')}
            </p>
            <div className="flex items-center justify-center space-x-2 rtl:space-x-reverse">
              <Calculator className="w-6 h-6 text-yellow-400" />
              <span className="text-lg">
                {language === 'en' ? 'Professional · Fast · Accurate' : 'مهني · سريع · دقيق'}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Progress Bar */}
      <div className="bg-white shadow-md sticky top-28 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  step <= currentStep 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {step}
                </div>
                <div className={`flex-1 h-1 mx-2 ${
                  step < currentStep ? 'bg-blue-600' : 'bg-gray-200'
                }`} />
                <span className={`text-sm font-medium ${
                  step <= currentStep ? 'text-blue-600' : 'text-gray-500'
                } ${isRTL ? 'font-arabic' : ''}`}>
                  {step === 1 && (language === 'en' ? 'Project' : 'المشروع')}
                  {step === 2 && (language === 'en' ? 'Contact' : 'الاتصال')}
                  {step === 3 && (language === 'en' ? 'Requirements' : 'المتطلبات')}
                  {step === 4 && (language === 'en' ? 'Review' : 'المراجعة')}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quote Form */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-2xl shadow-2xl p-8 md:p-12"
            >
              <div className={`mb-8 ${isRTL ? 'font-arabic text-right' : ''}`}>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  {language === 'en' ? `Step ${currentStep} of 4` : `الخطوة ${currentStep} من 4`}
                </h2>
                <p className="text-gray-600">
                  {currentStep === 1 && (language === 'en' ? 'Tell us about your project' : 'أخبرنا عن مشروعك')}
                  {currentStep === 2 && (language === 'en' ? 'Your contact information' : 'معلومات الاتصال الخاصة بك')}
                  {currentStep === 3 && (language === 'en' ? 'Product and material requirements' : 'متطلبات المنتج والمواد')}
                  {currentStep === 4 && (language === 'en' ? 'Review and submit your request' : 'راجع وأرسل طلبك')}
                </p>
              </div>

              <form onSubmit={currentStep === 4 ? handleSubmit : (e) => e.preventDefault()}>
                {renderStep()}

                <div className="flex justify-between mt-8">
                  <button
                    type="button"
                    onClick={prevStep}
                    disabled={currentStep === 1}
                    className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                      currentStep === 1
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-600 text-white hover:bg-gray-700'
                    }`}
                  >
                    {language === 'en' ? 'Previous' : 'السابق'}
                  </button>

                  {currentStep < 4 ? (
                    <button
                      type="button"
                      onClick={nextStep}
                      className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                    >
                      {language === 'en' ? 'Next' : 'التالي'}
                    </button>
                  ) : (
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-gradient-to-r from-green-600 to-green-700 text-white px-8 py-3 rounded-lg font-bold hover:from-green-700 hover:to-green-800 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 rtl:space-x-reverse"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                          <span>
                            {language === 'en' ? 'Submitting...' : 'جاري الإرسال...'}
                          </span>
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          <span>{t('quote.form.submit')}</span>
                        </>
                      )}
                    </motion.button>
                  )}
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Quote;